import { Copy, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Button,
  IconButton,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import type { NewAccessKey } from '@/api/s3';

/** A read-only value with a copy button. */
function CopyField({ label, value }: { label: string; value: string }) {
  const toast = useToast();
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-1)' }}>
      <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
        {label}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
        <code
          style={{
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            padding: 'var(--vn-space-2)',
            borderRadius: 'var(--vn-radius-sm)',
            background: 'var(--vn-bg-muted)',
            fontFamily: 'var(--vn-font-mono)',
            fontSize: 'var(--vn-font-size-sm)',
          }}
        >
          {value}
        </code>
        <IconButton
          variant="ghost"
          size="sm"
          aria-label={`Copy ${label}`}
          onClick={() => {
            navigator.clipboard.writeText(value).then(
              () => toast.success(`${label} copied`),
              () => toast.error('Copy failed'),
            );
          }}
        >
          <Copy size={15} />
        </IconButton>
      </div>
    </div>
  );
}

/** Shows a freshly-created key's secret exactly once — it can never be re-fetched. */
export function AccessKeySecretDialog({
  secret,
  onClose,
}: {
  secret: NewAccessKey | null;
  onClose: () => void;
}) {
  return (
    <Dialog open={secret !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Access key created</DialogTitle>
        </DialogHeader>
        <DialogBody style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          <div
            style={{
              display: 'flex',
              gap: 'var(--vn-space-2)',
              alignItems: 'flex-start',
              padding: 'var(--vn-space-3)',
              borderRadius: 'var(--vn-radius-md)',
              background: 'var(--vn-warning-subtle)',
              color: 'var(--vn-warning-text)',
              fontSize: 'var(--vn-font-size-sm)',
            }}
          >
            <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
            <span>Copy the secret now — it will not be shown again.</span>
          </div>
          <CopyField label="Access Key ID" value={secret?.access_key_id ?? ''} />
          <CopyField label="Secret Access Key" value={secret?.secret_access_key ?? ''} />
        </DialogBody>
        <DialogFooter>
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
